// Desktop HTML/CSS toolbar overlay. On desktop we have the DOM (unlike in
// the headset), so the UI is plain elements instead of canvas-texture panels.
// This module owns the DOM + styling; main.ts supplies the handlers (which
// drive the same uiState / setTopMode / etc. the VR side uses) and a
// getState() the module re-renders from after every action.

import type { ActionMode, ShapeMode } from "../interaction/tool";
import { createModeSwitch, createSegToggle } from "./mode-switch";
import { createActionBar } from "./action-bar";
import { tidySeparators } from "./dom";
import { keyForAction } from "./hotkeys";
import { paletteColorCss, paletteColorHex } from "./palette";

// Inline icons (stroke = currentColor so they flip to the dark ink on the blue
// .active button). Helper builds a 24x24 stroked SVG string.
const ICON_SVG = (inner: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ` +
  `stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;

// Tool icons. Metaphors mirror the VR icons in icons.ts where they exist:
// create = pencil, erase = eraser, move = 4-way arrows; paint = brush and
// select = dashed marquee are desktop-only additions. (Keyed by every
// DesktopTool; "resize" lives under Canvas, not the Tools row.)
const TOOL_ICONS: Record<"draw" | "paint" | "bucket" | "select" | "resize" | "move", string> = {
  // Pencil.
  draw: ICON_SVG(
    '<path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
  ),
  // Brush with a paint dab — the Paint (recolor) tool.
  paint: ICON_SVG(
    '<path d="m9.06 11.9 8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08"/>' +
      '<path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 ' +
      '1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z"/>',
  ),
  // Standard tilted paint bucket with a paint drip — the Paint Bucket (flood
  // fill) tool (the Lucide "paint-bucket" glyph).
  bucket: ICON_SVG(
    '<path d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"/>' +
      '<path d="m5 2 5 5"/><path d="M2 13h15"/>' +
      '<path d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"/>',
  ),
  // Dashed marquee — the Select tool.
  select: ICON_SVG(
    '<rect x="3.5" y="3.5" width="17" height="17" rx="2" stroke-dasharray="3.2 2.8"/>',
  ),
  // Crop-style frame — Resize the work volume.
  resize: ICON_SVG(
    '<path d="M6 3v15a1 1 0 0 0 1 1h15"/><path d="M18 21V6a1 1 0 0 0-1-1H2"/>',
  ),
  // Four-way arrows.
  move: ICON_SVG(
    '<path d="M5 9 2 12l3 3"/><path d="M9 5l3-3 3 3"/><path d="M15 19l-3 3-3-3"/>' +
      '<path d="M19 9l3 3-3 3"/><path d="M2 12h20"/><path d="M12 2v20"/>',
  ),
};
// Erase shares the "draw" tool (with the erase action), so it has no TOOL_ICONS
// key of its own — an angled eraser, mirroring the VR eraser metaphor.
const ICON_ERASE = ICON_SVG(
  '<path d="m7 21-4.3-4.3a1.5 1.5 0 0 1 0-2.1l9.6-9.6a1.5 1.5 0 0 1 2.1 0l5.6 5.6a1.5 1.5 0 0 1 0 2.1L13 21Z"/>' +
    '<path d="M22 21H7"/><path d="m5 11 9 9"/>',
);

// Pipette / eyedropper — the voxel-color sampler in the Edit color popover.
const ICON_PIPETTE = ICON_SVG(
  '<path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l9-9"/>' +
    '<path d="m15 6 3.4-3.4a2.1 2.1 0 0 1 3 3L18 9l.4.4a2.1 2.1 0 0 1 0 3 2.1 2.1 0 0 1-3 0l-3.8-3.8a2.1 2.1 0 0 1 0-3 2.1 2.1 0 0 1 3 0Z"/>',
);

// Shape-mode icons. Metaphors mirror the VR dial icons in icons.ts: box =
// hollow iso cube, voxel = flat solid square, extrude = footprint + up arrow,
// circle = disc, freeform = a hand-drawn blob.
const SHAPE_ICONS: Record<ShapeMode, string> = {
  // Iso cube cage: top rhombus, three down edges to the front, two front edges.
  box: ICON_SVG(
    '<path d="M12 3 21 8 21 16 12 21 3 16 3 8Z"/>' +
      '<path d="M3 8 12 13 21 8M12 13 12 21"/>',
  ),
  // Footprint slab with an arrow pulling straight up out of it.
  extrude: ICON_SVG(
    '<path d="M4 14 12 18 20 14 12 10Z"/>' +
      '<path d="M12 14 12 4"/><path d="M8.5 7 12 3.5 15.5 7"/>',
  ),
  // Flat unit cell — a face-on solid square, deliberately 2D so it never reads
  // as the 3D box cube.
  voxel: ICON_SVG(
    '<rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" fill-opacity="0.18"/>',
  ),
  // Disc laid on the surface — a true circle.
  circle: ICON_SVG(
    '<circle cx="12" cy="12" r="8.5" fill="currentColor" fill-opacity="0.18"/>',
  ),
  // Closed free-drawn blob — the only mode without a prior VR icon.
  freeform: ICON_SVG(
    '<path d="M8 5C4 6 3 11 5.5 14 7.5 16.3 6.5 19 10 20 14.5 21.3 21 17 20 11.5 19 6.5 13 3.5 8 5Z"/>',
  ),
};

// Reference-image controls (icon-only buttons; tooltips carry the word).
// Import… = the same upload-tray glyph the model/layout Import buttons use
// (action-bar.ts ICON_UPLOAD), for consistency; Edit = a frame with corner
// transform handles (Edit drags/scales/rotates the image, not freehand draw,
// so it gets a gizmo glyph rather than the Create pencil); Hide = eye + slash.
const ICON_REF_IMPORT = ICON_SVG(
  '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
    '<polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>',
);
const ICON_REF_EDIT = ICON_SVG(
  '<rect x="6.5" y="6.5" width="11" height="11" rx="1"/>' +
    '<path d="M3 3h3M3 3v3M21 3h-3M21 3v3M3 21h3M3 21v-3M21 21h-3M21 21v-3"/>',
);
const ICON_REF_HIDE = ICON_SVG(
  '<path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18 18 0 0 1-2.16 3.19"/>' +
    '<path d="M6.61 6.61A18 18 0 0 0 2 12s3 8 10 8a9.1 9.1 0 0 0 5.39-1.61"/>' +
    '<path d="M14.12 14.12A3 3 0 1 1 9.88 9.88"/><path d="M2 2l20 20"/>',
);

// Canvas controls (icon-only buttons; tooltips carry the word).
// Grid and Edges share the 3×3-lattice glyph but emphasize opposite parts:
// Grid de-emphasizes the outer border (the inner lattice is the subject),
// Edges de-emphasizes the inner lattice (the border is the subject). Fill and
// Clear are a filled vs empty iso cube. Resize = a box with measure-tick axes
// on two sides (drag the bounds); recenter = a crosshair (re-center the model
// in the work volume). Set dimensions has no button — clicking the canvas-size
// readout (#dt-canvas-size, top-left corner) opens the dimensions modal.
const ICON_RESIZE = ICON_SVG(
  '<rect x="7" y="7" width="14" height="14" rx="1"/>' +
    '<path d="M3 7v14"/><path d="M3 7h3"/><path d="M3 21h3"/>' +
    '<path d="M7 3h14"/><path d="M7 3v3"/><path d="M21 3v3"/>',
);
const ICON_FILL = ICON_SVG(
  '<path d="M12 3 21 8 21 16 12 21 3 16 3 8Z" fill="currentColor" fill-opacity="0.5"/>' +
    '<path d="M3 8 12 13 21 8M12 13 12 21"/>',
);
const ICON_CLEAR = ICON_SVG(
  '<path d="M12 3 21 8 21 16 12 21 3 16 3 8Z"/>' + '<path d="M3 8 12 13 21 8M12 13 12 21"/>',
);
const ICON_GRID = ICON_SVG(
  '<rect x="3" y="3" width="18" height="18" rx="2" stroke-opacity="0.3"/>' +
    '<path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/><path d="M15 3v18"/>',
);
const ICON_EDGES = ICON_SVG(
  '<rect x="3" y="3" width="18" height="18" rx="2"/>' +
    '<g stroke-opacity="0.3"><path d="M3 9h18"/><path d="M3 15h18"/>' +
    '<path d="M9 3v18"/><path d="M15 3v18"/></g>',
);
const ICON_RECENTER = ICON_SVG(
  '<circle cx="12" cy="12" r="3"/>' +
    '<path d="M12 2v4"/><path d="M12 18v4"/><path d="M2 12h4"/><path d="M18 12h4"/>',
);

// Desktop tools map 1:1 to TopModes (main does the mapping).
export type DesktopTool = "draw" | "paint" | "bucket" | "select" | "resize" | "move";

export interface DesktopUIState {
  tool: DesktopTool;
  action: ActionMode;
  shape: ShapeMode;
  symmetry: { x: boolean; y: boolean; z: boolean };
  gridOn: boolean;
  edgesOn: boolean;
  orthoOn: boolean;
  paletteIndex: number;
  referenceOn: boolean; // a reference image is loaded
  referenceEditing: boolean; // currently in image-transform mode
  referenceHidden: boolean; // manually hidden
  referenceOpacity: number; // 0..1
  frontLayerOn: boolean; // marquee selects only the front (non-passthrough) layer
  // Current canvas size: voxel cell counts and real-world size, preformatted
  // "W × D × H" (main owns the unit formatting).
  dims: { vox: string; real: string };
}

export interface DesktopUIHandlers {
  setTool(t: DesktopTool): void;
  setAction(a: ActionMode): void;
  setShape(s: ShapeMode): void;
  toggleSymmetry(axis: "x" | "y" | "z"): void;
  rotateSelection(axis: "x" | "y" | "z", deg: number): void;
  flipSelection(axis: "x" | "y" | "z"): void;
  invertSelection(): void;
  toggleFrontLayer(): void;
  toggleGrid(): void;
  toggleEdges(): void;
  toggleProjection(): void;
  setPalette(i: number): void;
  // Recolor swatch i to the given "#rrggbb" string (live, persisted).
  editPalette(i: number, hex: string): void;
  // Restore all swatches to their factory defaults.
  resetPalette(): void;
  // Arm the voxel eyedropper: the next click on a voxel calls back with its
  // exact stored "#rrggbb" (the value that painted it, not the lit screen
  // pixel). cancelEyedropper disarms it (editor closed / toggled off).
  beginEyedropper(onSample: (hex: string) => void): void;
  cancelEyedropper(): void;
  fillCanvas(): void;
  clearCanvas(): void;
  saveModel(): void;
  saveModelAs(): void;
  newModel(): void;
  importModel(): void;
  recenter(): void;
  openDimensions(): void;
  enterFloorPlan(): void;
  importReference(file: File): void;
  toggleReferenceEdit(): void;
  toggleReferenceHidden(): void;
  setReferenceOpacity(v: number): void;
}

export interface DesktopUIOptions {
  handlers: DesktopUIHandlers;
  getState: () => DesktopUIState;
  // CSS color per palette index; index 0 is "empty" and is skipped.
  paletteCss: string[];
}

const CSS = `
#dt-ui {
  position: fixed; top: 0; left: 0; bottom: 0; z-index: 10;
  width: 192px; box-sizing: border-box;
  display: flex; flex-direction: column; align-items: stretch; gap: 12px;
  padding: 12px;  /* bottom inset is set live to the dock height (collection-dock.ts) */
  overflow-y: auto;
  font: 13px/1.2 system-ui, -apple-system, sans-serif;
  color: #e7e9f0;
  background: linear-gradient(90deg, rgba(26,27,38,0.96), rgba(20,21,30,0.92));
  border-right: 1px solid rgba(255,255,255,0.08);
  user-select: none;
}
/* Each group: a full-width label sub-header, buttons wrapping below it. */
#dt-ui .grp { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; }
#dt-ui .sep { height: 1px; width: auto; background: rgba(255,255,255,0.12); margin: 1px 0; }
#dt-ui .lbl {
  flex-basis: 100%; color: #8b90a3; font-size: 11px; text-transform: uppercase;
  letter-spacing: .04em; margin: 0 0 1px;
}
#dt-ui button {
  font: inherit; color: #d7dae6; cursor: pointer;
  background: #34384a; border: 1px solid rgba(255,255,255,0.1);
  border-radius: 7px; padding: 5px 10px;
}
#dt-ui button:hover { background: #424862; }
#dt-ui button.active { background: #6fa0ff; color: #11141c; border-color: #6fa0ff; }
#dt-ui button.on { background: #f2c14e; color: #1a1206; border-color: #f2c14e; }
#dt-ui .sw { width: 20px; height: 20px; padding: 0; border-radius: 5px; }
#dt-ui .sw.active { outline: 2px solid #fff; outline-offset: 1px; }
/* Canvas size readout — text on its own line under the top-left corner
   cluster (settings gear / VR edit / VR walkthrough). It's a flex child of
   #corner-controls; flex-basis:100% drops it below the button row so it tracks
   the cluster wherever it sits. A text-shadow keeps it legible over the scene.
   It's a <button> (clicking it opens the dimensions modal), styled as plain
   text — chromeless, with a hover brighten as the only affordance. */
#corner-controls { flex-wrap: wrap; }
/* NB: id is dt-canvas-size, NOT dt-dims — the latter is the dimensions modal
   (dimensions-panel.ts); reusing it collides and inherits the modal's pill CSS. */
#dt-canvas-size {
  flex-basis: 100%; margin: 1px 0 0; padding: 0;
  background: none; border: 0; text-align: left; cursor: pointer;
  font: 11px/1.5 system-ui, -apple-system, sans-serif; color: #aeb3c4;
  font-variant-numeric: tabular-nums; user-select: none;
  text-shadow: 0 1px 3px rgba(0,0,0,0.7);
}
#dt-canvas-size b { color: #d7dae6; font-weight: 600; }
#dt-canvas-size:hover { color: #d7dae6; }
#dt-canvas-size:hover b { color: #fff; }
#dt-ui .hidden { display: none; }
/* Tool + shape pickers: square icon buttons (tooltip carries the name). */
#dt-ui .icon-btn {
  width: 28px; height: 28px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
}
#dt-ui .icon-btn svg { width: 19px; height: 19px; display: block; }
/* flex-shrink:0 is load-bearing: this is a flex-column child with overflow:hidden,
   which the flexbox spec gives an automatic min-height of 0 — so when the sidebar
   overflows the window it would otherwise collapse to a sliver and clip the name.
   Pinning shrink keeps its natural height; the overflow goes to #dt-ui's scroller. */
#dt-ui .docname { font-size: 12px; flex-shrink: 0; font-weight: 600; color: #cdd2de; text-align: center; padding: 1px 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#dt-ui .docname.muted { font-weight: 400; font-style: italic; color: rgba(231,233,240,0.45); }
/* Swatch recolor popover (right-click a color). Themed like the context menu. */
#dt-colorpop {
  position: fixed; z-index: 60; padding: 10px; width: 160px; box-sizing: border-box;
  background: rgba(28,30,38,0.98); border: 1px solid rgba(255,255,255,0.14);
  border-radius: 10px; box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  font: 13px/1.3 system-ui, -apple-system, sans-serif; color: #d7dae6;
  display: flex; flex-direction: column; gap: 8px; user-select: none;
}
#dt-colorpop .ttl { color: #8b90a3; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
#dt-colorpop input[type=color] {
  width: 100%; height: 34px; padding: 0; cursor: pointer;
  background: transparent; border: 1px solid rgba(255,255,255,0.12); border-radius: 6px;
}
#dt-colorpop .row { display: flex; gap: 6px; }
#dt-colorpop button {
  flex: 1; font: inherit; color: #d7dae6; cursor: pointer;
  background: #34384a; border: 1px solid rgba(255,255,255,0.1);
  border-radius: 7px; padding: 5px 10px;
}
#dt-colorpop button:hover { background: #424862; }
#dt-colorpop button.primary { background: #6fa0ff; color: #11141c; border-color: #6fa0ff; }
/* Color box + eyedropper share one row: the dropper is a fixed square to the
   left, the color box flexes to fill the rest (shortened to make room). */
#dt-colorpop .pickrow { display: flex; gap: 6px; align-items: stretch; }
#dt-colorpop .pickrow input[type=color] { flex: 1; min-width: 0; }
/* Eyedropper (sample from voxel): square icon-only button; lights up while armed. */
#dt-colorpop button.dropper {
  flex: 0 0 34px; width: 34px; height: 34px; padding: 0;
  display: flex; align-items: center; justify-content: center;
}
#dt-colorpop button.dropper svg { width: 17px; height: 17px; display: block; }
#dt-colorpop button.dropper.active { background: #6fa0ff; color: #11141c; border-color: #6fa0ff; }
/* While armed, the canvas reads as a sampler. */
body.dt-eyedropping, body.dt-eyedropping canvas { cursor: crosshair !important; }
`;

export function installDesktopUI(opts: DesktopUIOptions): {
  setVisible(v: boolean): void;
  render(): void;
  flashSaved(): void;
  setUnsaved(dirty: boolean): void;
  setName(name: string): void;
  refreshSwatches(): void;
} {
  const { handlers, getState, paletteCss } = opts;

  const style = document.createElement("style");
  style.textContent = CSS;
  document.head.appendChild(style);

  const root = document.createElement("div");
  root.id = "dt-ui";
  document.body.appendChild(root);

  const mk = (
    tag: string,
    cls?: string,
    text?: string,
  ): HTMLElement => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text) el.textContent = text;
    return el;
  };
  const group = (label?: string): HTMLElement => {
    const g = mk("div", "grp");
    if (label) g.appendChild(mk("span", "lbl", label));
    root.appendChild(g);
    return g;
  };
  const sep = () => root.appendChild(mk("div", "sep"));
  const button = (
    parent: HTMLElement,
    text: string,
    onClick: () => void,
    cls = "",
  ): HTMLButtonElement => {
    const b = mk("button", cls, text) as HTMLButtonElement;
    b.addEventListener("click", () => {
      onClick();
      render();
    });
    parent.appendChild(b);
    return b;
  };

  // ── Mode toggle (top) ─────────────────────────────────────────────────
  root.appendChild(
    createModeSwitch("modeling", (m) => {
      if (m === "floorplan") handlers.enterFloorPlan();
    }),
  );
  // Save / Save As / New / Import the current MODEL.
  const actbar = createActionBar({
    noun: "model",
    onSave: () => handlers.saveModel(),
    onSaveAs: () => handlers.saveModelAs(),
    onNew: () => handlers.newModel(),
    onImport: () => handlers.importModel(),
  });
  const nameLabel = document.createElement("div");
  nameLabel.className = "docname muted";
  nameLabel.textContent = "Untitled model";
  root.appendChild(nameLabel);
  root.appendChild(actbar.el);
  sep();

  // ── View (above the tools) ───────────────────────────────────────────
  // 2D = orthographic, 3D = the default perspective view.
  const viewGrp = group("View");
  const viewToggle = createSegToggle(
    [
      { label: "2D", value: "2d" },
      { label: "3D", value: "3d" },
    ],
    getState().orthoOn ? "2d" : "3d",
    (v) => {
      if ((v === "2d") !== getState().orthoOn) handlers.toggleProjection();
    },
  );
  viewToggle.el.style.flexBasis = "100%"; // full-width segmented control
  viewGrp.appendChild(viewToggle.el);
  sep();

  // ── Tools / sketch options (one section) ─────────────────────────────
  // Tools, Shape/Color, Mirror, and Select options share a single divider-
  // less section: the tool buttons sit directly above the options they drive.
  // Add/Erase enter sketch mode with the paint/erase action; Select selects.
  // Resize lives under Canvas (it resizes the work volume).
  const toolsGrp = group("Tools");
  // Select leads as the neutral, non-destructive default (matches Layout mode);
  // Create follows so the primary build tool stays prominent, with its Erase /
  // Paint siblings beside it. Move (reposition the selection) trails.
  const selectBtn = button(toolsGrp, "", () => handlers.setTool("select"), "icon-btn");
  selectBtn.innerHTML = TOOL_ICONS.select;
  const addBtn = button(toolsGrp, "", () => {
    handlers.setTool("draw");
    handlers.setAction("draw");
  }, "icon-btn");
  addBtn.innerHTML = TOOL_ICONS.draw;
  const eraseBtn = button(toolsGrp, "", () => {
    handlers.setTool("draw");
    handlers.setAction("erase");
  }, "icon-btn");
  eraseBtn.innerHTML = ICON_ERASE;
  // Paint recolors existing voxels with the same Shape options as Create.
  const paintBtn = button(toolsGrp, "", () => handlers.setTool("paint"), "icon-btn");
  paintBtn.innerHTML = TOOL_ICONS.paint;
  // Paint Bucket flood-fills the connected same-color region with the active color.
  const bucketBtn = button(toolsGrp, "", () => handlers.setTool("bucket"), "icon-btn");
  bucketBtn.innerHTML = TOOL_ICONS.bucket;
  const moveBtn = button(toolsGrp, "", () => handlers.setTool("move"), "icon-btn");
  moveBtn.innerHTML = TOOL_ICONS.move;

  // Sketch options (shape, color) — shown while Add or Erase is active.
  const sketchGrp = group();
  // Shape label + buttons hide for Bucket (it floods a region, ignoring shape) —
  // only the Color row stays. Captured so render can toggle the sub-row alone.
  const shapeLabel = mk("span", "lbl", "Shape");
  sketchGrp.appendChild(shapeLabel);
  const shapeBtns = {} as Record<ShapeMode, HTMLButtonElement>;
  for (const m of ["box", "extrude", "voxel", "circle", "freeform"] as ShapeMode[]) {
    const b = button(sketchGrp, "", () => handlers.setShape(m), "icon-btn");
    b.innerHTML = SHAPE_ICONS[m];
    shapeBtns[m] = b;
  }
  // Color is hidden while erasing (erase places no color — see render).
  const colorLabel = mk("span", "lbl", "Color");
  sketchGrp.appendChild(colorLabel);
  // Recolor popover: one at a time. Live-previews onto the model/3D as you
  // pick; Cancel (or Escape) reverts to the color the swatch had on open, OK
  // (or clicking away) keeps it.
  let colorEditor: { el: HTMLElement; cleanup: () => void } | null = null;
  function closeColorEditor(): void {
    if (!colorEditor) return;
    colorEditor.cleanup();
    colorEditor.el.remove();
    colorEditor = null;
  }
  function openColorEditor(
    index: number,
    swatch: HTMLButtonElement,
    x: number,
    y: number,
  ): void {
    closeColorEditor();
    const before = paletteColorHex(index);
    // Editing a swatch must NOT recolor voxels already painted with it (that
    // happens on COMMIT, where existing voxels are frozen to keep their color —
    // see canvas-ops.recolorSwatch). So picking is preview-only: it just tints
    // the swatch button; the model is left alone until the editor commits.
    const preview = (hex: string): void => {
      swatch.style.background = hex;
    };
    let committed = false;
    const commit = (): void => {
      committed = true;
      if (input.value.toLowerCase() !== before.toLowerCase()) {
        handlers.editPalette(index, input.value);
      }
    };

    const el = mk("div");
    el.id = "dt-colorpop";
    el.appendChild(mk("div", "ttl", `Edit color ${index}`));

    // Eyedropper: sample the EXACT stored color of an existing voxel. Unlike the
    // OS color picker's built-in dropper (which reads the lit screen pixel and
    // so drifts from the real paint color), this reads the voxel's palette value
    // straight from the grid. While armed, canvas clicks are intercepted by the
    // desktop input seam — the popover stays open and the body shows a crosshair.
    // Icon-only button sits left of the (shortened) color box on one row.
    let sampling = false;
    const dropBtn = mk("button", "dropper") as HTMLButtonElement;
    dropBtn.innerHTML = ICON_PIPETTE;
    dropBtn.title = "Sample from voxel — click a voxel to copy its exact color";
    const input = mk("input") as HTMLInputElement;
    input.type = "color";
    input.value = before;
    input.addEventListener("input", () => preview(input.value));
    const setSampling = (on: boolean): void => {
      sampling = on;
      dropBtn.classList.toggle("active", on);
      document.body.classList.toggle("dt-eyedropping", on);
      if (on) {
        handlers.beginEyedropper((hex) => {
          input.value = hex;
          preview(hex);
          setSampling(false);
        });
      } else {
        handlers.cancelEyedropper();
      }
    };
    dropBtn.addEventListener("click", () => setSampling(!sampling));
    const pickRow = mk("div", "pickrow");
    pickRow.append(dropBtn, input);
    el.appendChild(pickRow);

    const row = mk("div", "row");
    const cancelBtn = mk("button", "", "Cancel") as HTMLButtonElement;
    cancelBtn.addEventListener("click", () => {
      closeColorEditor(); // committed stays false → cleanup reverts the preview
      render();
    });
    const okBtn = mk("button", "primary", "OK") as HTMLButtonElement;
    okBtn.addEventListener("click", () => {
      commit();
      closeColorEditor();
      render();
    });
    row.append(cancelBtn, okBtn);
    el.appendChild(row);
    document.body.appendChild(el);

    // Position at the cursor, nudged back on screen if it would overflow.
    const r = el.getBoundingClientRect();
    el.style.left = `${Math.min(x, window.innerWidth - r.width - 8)}px`;
    el.style.top = `${Math.min(y, window.innerHeight - r.height - 8)}px`;

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        // While sampling, Escape just disarms the dropper (keeps the editor open)
        // so it never strands you in eyedropper mode.
        if (sampling) {
          setSampling(false);
          return;
        }
        closeColorEditor(); // revert (no commit)
        render();
      }
    };
    // Clicking outside commits the change; revert is via Cancel/Escape. While
    // sampling, the click is a voxel sample (handled by the input seam) — it must
    // NOT dismiss the popover, so ignore outside-clicks then.
    const onDown = (e: MouseEvent): void => {
      if (sampling) return;
      if (!el.contains(e.target as Node)) {
        commit();
        closeColorEditor();
        render();
      }
    };
    document.addEventListener("keydown", onKey);
    // Defer the outside-click listener so the opening right-click doesn't
    // immediately dismiss it.
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    colorEditor = {
      el,
      cleanup: () => {
        clearTimeout(t);
        document.removeEventListener("keydown", onKey);
        document.removeEventListener("mousedown", onDown);
        // Closing the editor (any path) must never leave the dropper armed.
        setSampling(false);
        // If we didn't commit, undo the preview tint on the swatch button.
        if (!committed) swatch.style.background = before;
      },
    };
  }

  const swatches: HTMLButtonElement[] = [];
  for (let i = 1; i < paletteCss.length; i++) {
    const idx = i;
    const b = button(sketchGrp, "", () => handlers.setPalette(idx), "sw");
    b.style.background = paletteCss[idx];
    b.title = `Color ${idx} — left-click to use, right-click to edit`;
    // Single click selects (above); right-click opens the recolor popover.
    b.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      openColorEditor(idx, b, e.clientX, e.clientY);
    });
    swatches.push(b);
  }
  // Reset every swatch to its factory default.
  const resetColorsBtn = button(
    sketchGrp,
    "Reset colors",
    () => handlers.resetPalette(),
  );
  resetColorsBtn.style.flexBasis = "100%";

  // ── Mirror ───────────────────────────────────────────────────────────
  // Its own always-visible category: symmetry applies across every tool —
  // drawing, erasing, selecting, and moving — not just sketch. Pinned to the
  // foot of the tools/options section (see optionsSep below) so it keeps one
  // fixed home regardless of the active tool.
  const mirrorGrp = group("Mirror");
  const symBtns: Record<"x" | "y" | "z", HTMLButtonElement> = {
    x: button(mirrorGrp, "X", () => handlers.toggleSymmetry("x")),
    y: button(mirrorGrp, "Y", () => handlers.toggleSymmetry("y")),
    z: button(mirrorGrp, "Z", () => handlers.toggleSymmetry("z")),
  };

  // ── Select options (rotate) ──────────────────────────────────────────
  // Clicking a selected voxel moves it; deletion is Backspace.
  const selGrp = group("Select");
  // Invert: select every voxel that isn't currently selected (none selected →
  // select all).
  const invertBtn = button(selGrp, "Invert selection", () => handlers.invertSelection());
  invertBtn.style.flexBasis = "100%";
  // Rotate the selection by the entered angle about a grid axis, re-voxelizing —
  // the way to build an angled feature (model straight, select, rotate to angle).
  selGrp.appendChild(mk("span", "lbl", "Rotate (°)"));
  const rotAngle = document.createElement("input");
  rotAngle.type = "number";
  rotAngle.step = "5";
  rotAngle.value = "15";
  rotAngle.style.cssText = "flex:0 0 52px; min-width:0;";
  selGrp.appendChild(rotAngle);
  const rotAxis = (label: "x" | "y" | "z") =>
    button(selGrp, label.toUpperCase(), () =>
      handlers.rotateSelection(label, Number(rotAngle.value) || 0),
    );
  rotAxis("x");
  rotAxis("y");
  rotAxis("z");
  // Flip (mirror) the selection about its centre on an axis — lossless. Click
  // more than one to flip on multiple axes.
  selGrp.appendChild(mk("span", "lbl", "Flip"));
  const flipAxis = (label: "x" | "y" | "z") =>
    button(selGrp, label.toUpperCase(), () => handlers.flipSelection(label));
  flipAxis("x");
  flipAxis("y");
  flipAxis("z");
  // Marquee: front-layer-only (non-passthrough) select. Also via Alt-drag.
  selGrp.appendChild(mk("span", "lbl", "Marquee (Alt)"));
  const frontLayerBtn = button(selGrp, "Front layer only", () =>
    handlers.toggleFrontLayer(),
  );
  frontLayerBtn.style.flexBasis = "100%";
  // Divider closing the Tools/options section. Mirror is pinned just above it
  // so it always sits at the foot of this section, below whichever contextual
  // options (Shape/Color or Select) are currently showing.
  const optionsSep = sep();
  root.insertBefore(mirrorGrp, optionsSep);

  // ── Canvas (work-volume size + whole-canvas ops) ─────────────────────
  // The Resize tool (drag the work-volume walls) sits here with the canvas's
  // dimensions, the fill/clear actions, and Recenter. The grid/edges display
  // toggles moved to their own "Show" section below (mirrors Layout mode) so
  // they don't sit beside the destructive Fill/Clear actions.
  const canvasGrp = group("Canvas");
  const iconBtn = (
    parent: HTMLElement,
    icon: string,
    label: string,
    onClick: () => void,
  ): HTMLButtonElement => {
    const b = button(parent, "", onClick, "icon-btn");
    b.innerHTML = icon;
    b.title = label;
    b.setAttribute("aria-label", label);
    return b;
  };
  const resizeBtn = iconBtn(canvasGrp, ICON_RESIZE, "Resize", () => handlers.setTool("resize"));
  iconBtn(canvasGrp, ICON_FILL, "Fill", () => handlers.fillCanvas());
  iconBtn(canvasGrp, ICON_CLEAR, "Clear", () => handlers.clearCanvas());
  iconBtn(canvasGrp, ICON_RECENTER, "Recenter", () => handlers.recenter());
  // The live canvas-size readout no longer sits in the toolkit — it appends to
  // the top-left corner cluster (gear / VR buttons) and wraps onto its own line
  // beneath them (see #dt-canvas-size). Falls back to <body> if the cluster is
  // absent. (id is NOT dt-dims — that's the dimensions modal in dimensions-panel.)
  // It doubles as the "Set dimensions" affordance: clicking it opens the
  // dimensions modal (there is no separate button in the Canvas group).
  const dimsReadout = document.createElement("button");
  dimsReadout.id = "dt-canvas-size";
  dimsReadout.type = "button";
  dimsReadout.title = "Set dimensions…";
  dimsReadout.setAttribute("aria-label", "Set dimensions");
  dimsReadout.addEventListener("click", () => handlers.openDimensions());
  (document.getElementById("corner-controls") ?? document.body).appendChild(dimsReadout);
  sep();

  // ── Show (display toggles) ───────────────────────────────────────────
  // Grid/edges visibility — non-destructive view aids, kept out of Canvas so
  // they don't sit beside the destructive Fill/Clear actions. Parallels the
  // Layout "Show" section's place near the foot of the panel.
  const showGrp = group("Show");
  const gridBtn = iconBtn(showGrp, ICON_GRID, "Grid", () => handlers.toggleGrid());
  const edgesBtn = iconBtn(showGrp, ICON_EDGES, "Edges", () => handlers.toggleEdges());
  sep();

  // ── Reference image ──────────────────────────────────────────────────
  // Import a top-down picture, then Edit to drag/scale/rotate it in the view
  // (best in ortho top-down) and carve the model's outline to match.
  const refGrp = group("Reference image");
  const refFile = document.createElement("input");
  refFile.type = "file";
  refFile.accept = "image/*";
  refFile.style.display = "none";
  refFile.addEventListener("change", () => {
    const f = refFile.files?.[0];
    if (f) handlers.importReference(f);
    refFile.value = ""; // allow re-importing the same file
  });
  root.appendChild(refFile);
  const refImportBtn = button(refGrp, "", () => refFile.click(), "icon-btn");
  refImportBtn.innerHTML = ICON_REF_IMPORT;
  refImportBtn.title = "Import…";
  refImportBtn.setAttribute("aria-label", "Import reference image");
  const refEditBtn = button(refGrp, "", () => handlers.toggleReferenceEdit(), "icon-btn");
  refEditBtn.innerHTML = ICON_REF_EDIT;
  refEditBtn.title = "Edit";
  refEditBtn.setAttribute("aria-label", "Edit reference image");
  const refHideBtn = button(refGrp, "", () => handlers.toggleReferenceHidden(), "icon-btn");
  refHideBtn.innerHTML = ICON_REF_HIDE;
  refHideBtn.title = "Hide";
  refHideBtn.setAttribute("aria-label", "Hide reference image");
  // No Clear button — Backspace (while editing the reference) removes it.
  const refOpacity = document.createElement("input");
  refOpacity.type = "range";
  refOpacity.min = "0";
  refOpacity.max = "1";
  refOpacity.step = "0.05";
  // Full-width but contained: range inputs don't shrink by default and would
  // spill past the sidebar without box-sizing + min-width:0.
  refOpacity.style.cssText =
    "flex-basis:100%; width:100%; min-width:0; box-sizing:border-box; margin:0;";
  refOpacity.addEventListener("input", () =>
    handlers.setReferenceOpacity(Number(refOpacity.value)),
  );
  refGrp.appendChild(refOpacity);
  sep();

  // Save / New / Import all live in the bottom model dock's action cluster now.
  // Settings live in the floating gear (both modes). Undo/redo are keyboard-only
  // (Cmd/Ctrl+Z / Shift+Z / Y) and, in VR, the controller face buttons.

  // ── Render: reflect current state into active/visible classes ─────────
  function setActive(el: HTMLElement, on: boolean): void {
    el.classList.toggle("active", on);
  }
  // Tooltip "<label> (<key>)" reflecting the current (user-rebindable) hotkey, so
  // the hint stays correct after a rebind. No key bound → just the label.
  function hint(
    el: HTMLButtonElement,
    label: string,
    actionId: string,
    desc?: string, // extra tooltip line (title only; aria-label stays the name)
  ): void {
    const k = keyForAction(actionId);
    const base = k ? `${label} (${k.toUpperCase()})` : label;
    el.title = desc ? `${base} — ${desc}` : base;
    // Icon-only buttons (Tools, Shape) carry no text — expose the name to
    // assistive tech and to the smoke test via aria-label.
    el.setAttribute("aria-label", label);
  }
  function render(): void {
    const s = getState();
    // Add/Erase are sketch mode distinguished by the paint/erase action.
    setActive(addBtn, s.tool === "draw" && s.action === "draw");
    setActive(eraseBtn, s.tool === "draw" && s.action === "erase");
    setActive(paintBtn, s.tool === "paint");
    setActive(bucketBtn, s.tool === "bucket");
    setActive(selectBtn, s.tool === "select");
    setActive(moveBtn, s.tool === "move");
    setActive(resizeBtn, s.tool === "resize");
    hint(addBtn, "Create", "tool-create");
    hint(eraseBtn, "Erase", "tool-erase");
    hint(paintBtn, "Paint", "tool-paint");
    hint(
      bucketBtn,
      "Paint Bucket",
      "tool-bucket",
      "flood-fill the connected same-color region (Shift: recolor every voxel of that color)",
    );
    hint(selectBtn, "Select", "tool-select");
    hint(moveBtn, "Move", "tool-move");
    hint(shapeBtns.box, "Box", "shape-box");
    hint(shapeBtns.extrude, "Extrude", "shape-extrude");
    hint(shapeBtns.voxel, "Voxel", "shape-voxel");
    hint(shapeBtns.circle, "Circle", "shape-circle");
    hint(
      shapeBtns.freeform,
      "Freeform",
      "shape-freeform",
      "click to place points, click the first point or Enter to close, Esc to cancel",
    );
    hint(invertBtn, "Select everything that isn't selected", "select-invert");
    for (const k of ["box", "extrude", "voxel", "circle", "freeform"] as ShapeMode[]) {
      setActive(shapeBtns[k], s.shape === k);
    }
    symBtns.x.classList.toggle("on", s.symmetry.x);
    symBtns.y.classList.toggle("on", s.symmetry.y);
    symBtns.z.classList.toggle("on", s.symmetry.z);
    gridBtn.classList.toggle("on", s.gridOn);
    edgesBtn.classList.toggle("on", s.edgesOn);
    viewToggle.setActive(s.orthoOn ? "2d" : "3d");
    const vk = keyForAction("view-toggle");
    viewToggle.el.title = vk ? `Toggle 2D / 3D (${vk.toUpperCase()})` : "Toggle 2D / 3D";
    // Reference tools show in both 2D and 3D (the overlay works in either).
    refGrp.classList.remove("hidden");
    refEditBtn.classList.toggle("on", s.referenceEditing);
    refHideBtn.classList.toggle("on", s.referenceHidden);
    refEditBtn.disabled = !s.referenceOn || s.referenceHidden;
    refHideBtn.disabled = !s.referenceOn;
    refOpacity.disabled = !s.referenceOn;
    if (document.activeElement !== refOpacity) refOpacity.value = String(s.referenceOpacity);
    dimsReadout.innerHTML =
      `Voxels <b>${s.dims.vox}</b><br>Size <b>${s.dims.real}</b>`;
    // Refresh active ring + background (so runtime recolors / Reset show up).
    swatches.forEach((b, idx) => {
      setActive(b, idx + 1 === s.paletteIndex);
      b.style.background = paletteColorCss(idx + 1);
    });
    // Color is meaningless while erasing — hide the whole color row then.
    const erasing = s.tool === "draw" && s.action === "erase";
    colorLabel.style.display = erasing ? "none" : "";
    for (const b of swatches) b.style.display = erasing ? "none" : "";
    resetColorsBtn.style.display = erasing ? "none" : "";
    // Contextual sections: shape/color options while Create/Erase OR Paint is
    // active (Paint shares the same shapes + palette); Bucket shows only the
    // Color row (it floods a region — shape doesn't apply); Select options only
    // while selecting.
    sketchGrp.classList.toggle(
      "hidden",
      s.tool !== "draw" && s.tool !== "paint" && s.tool !== "bucket",
    );
    const hideShape = s.tool === "bucket";
    shapeLabel.style.display = hideShape ? "none" : "";
    for (const b of Object.values(shapeBtns)) b.style.display = hideShape ? "none" : "";
    setActive(frontLayerBtn, s.frontLayerOn);
    selGrp.classList.toggle("hidden", s.tool !== "select");
    // Mirror is pinned at the foot of the tools/options section (see its
    // insertBefore at construction) — no per-tool reparenting.
    tidySeparators(root); // collapse dividers left adjacent by hidden sections
  }

  render();

  return {
    setVisible(v: boolean): void {
      root.style.display = v ? "flex" : "none";
      // The corner readout lives on <body>, not in the sidebar — toggle it too
      // so it vanishes in floor mode.
      dimsReadout.style.display = v ? "block" : "none";
    },
    render,
    flashSaved: () => actbar.flashSaved(),
    setUnsaved: (dirty: boolean) => actbar.setUnsaved(dirty),
    setName(name: string): void {
      nameLabel.textContent = name || "Untitled model";
      nameLabel.classList.toggle("muted", !name);
    },
    // Repaint every swatch background from the live palette — used when a model
    // load swaps the whole palette out from under the (statically-built) row.
    // swatches[k] is palette index k+1 (the row skips the reserved index 0).
    refreshSwatches(): void {
      swatches.forEach((b, k) => {
        b.style.background = paletteColorCss(k + 1);
      });
    },
  };
}
